import React, { useEffect, useState } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Box, Flex, Badge } from "@chakra-ui/react";
import { Eye, Archive, Plus, EllipsisVertical, ShieldAlert, Edit } from "lucide-react";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import GradientButton from "components/Buttons/GradientButton";

import AddProtectionTierDrawer from "./components/AddProtectionTierDrawer";
import ViewProtectionTierDrawer from "./components/ViewProtectionTierDrawer";
import EditProtectionTierDrawer from "./components/EditProtectionTierDrawer";
import DeleteProtectionTierModal from "./components/DeleteProtectionTierModal";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchProtectionTiersAction,
  createProtectionTierAction,
  updateProtectionTierAction,
  deleteProtectionTierAction,
} from "../../../state/actions/protectionTierActions";

import { useCustomNotification } from "components/Toasts/OpenNotification";

const ManageProtectionTiersTab = () => {
  const dispatch = useDispatch();
  const notify = useCustomNotification();

  const { protectionTiers, loading } = useSelector((state) => state.protectionTiers);

  const [isAddDrawerOpen, setAddDrawerOpen] = useState(false);
  const [isViewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setEditDrawerOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  useEffect(() => {
    dispatch(fetchProtectionTiersAction());
  }, [dispatch]);

  const openAddDrawer = () => setAddDrawerOpen(true);
  const closeAddDrawer = () => setAddDrawerOpen(false);

  const openViewDrawer = (tier) => {
    setSelectedTier(tier);
    setViewDrawerOpen(true);
  };

  const openEditDrawer = (tier) => {
    setSelectedTier(tier);
    setEditDrawerOpen(true);
  };

  const openDeleteModal = (tier) => {
    setSelectedTier(tier);
    setDeleteModalOpen(true);
  };

  const handleAddTier = async (data) => {
    const response = await dispatch(createProtectionTierAction(data));

    if (response.success) {
      closeAddDrawer();
      notify({
        type: "success",
        message: "Created",
        description: "Protection tier created successfully.",
      });
    } else {
      notify({
        type: "error",
        message: "Error",
        description: response.error || "Failed to create protection tier.",
      });
    }
  };

  const handleUpdateTier = async (data) => {
    if (!selectedTier) return;

    const response = await dispatch(updateProtectionTierAction(selectedTier.id, data));

    if (response.success) {
      setEditDrawerOpen(false);
      notify({
        type: "success",
        message: "Updated",
        description: "Protection tier updated successfully.",
      });
    } else {
      notify({
        type: "error",
        message: "Error",
        description: response.error || "Failed to update protection tier.",
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedTier) return;

    const response = await dispatch(deleteProtectionTierAction(selectedTier.id));

    if (response.success) {
      setDeleteModalOpen(false);
      notify({
        type: "success",
        message: "Deleted",
        description: "Protection tier deleted successfully.",
      });
    } else {
      notify({
        type: "error",
        message: "Error",
        description: response.error || "Failed to delete protection tier.",
      });
    }
  };

  const generateMenuItems = (row) => [
    {
      icon: <Eye size={16} />,
      text: "View Details",
      onClick: () => openViewDrawer(row.original),
    },
    {
      icon: <Edit size={16} />,
      text: "Edit",
      onClick: () => openEditDrawer(row.original),
    },
    {
      icon: <Archive size={16} />,
      text: "Delete",
      onClick: () => openDeleteModal(row.original),
      extraIcon: <ShieldAlert size={16} color="pink" />,
    },
  ];

  const columnsData = [
    { Header: "Min Value", accessor: "min_value" },
    { Header: "Max Value", accessor: "max_value" },
    { Header: "Rate", accessor: "rate" },
    {
      Header: "Created",
      accessor: "created_at",
      Cell: ({ value }) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      Header: "Updated",
      accessor: "updated_at",
      Cell: ({ value }) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={generateMenuItems(row)}
        />
      ),
    },
  ];

  return (
    <Box>
      <Flex justifyContent="end" mb={4} pt={{ base: "120px", md: "50px" }}>
        {loading ? (
          <Skeleton width="180px" height="40px" borderRadius="md" />
        ) : (
          <GradientButton
            icon={<Plus size={16} />}
            height="40px"
            mr={5}
            onClick={openAddDrawer}
          >
            Add Protection Tier
          </GradientButton>
        )}
      </Flex>

      <ReusableTable
        columnsData={columnsData}
        tableData={protectionTiers || []}
        isLoading={loading}
      />

      <AddProtectionTierDrawer
        isOpen={isAddDrawerOpen}
        onClose={closeAddDrawer}
        onCreate={handleAddTier}
      />

      <ViewProtectionTierDrawer
        isOpen={isViewDrawerOpen}
        onClose={() => setViewDrawerOpen(false)}
        tier={selectedTier}
      />

      <EditProtectionTierDrawer
        isOpen={isEditDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        tier={selectedTier}
        onUpdate={handleUpdateTier}
      />

      <DeleteProtectionTierModal
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        tier={selectedTier}
      />
    </Box>
  );
};

export default ManageProtectionTiersTab;